import {
  CURRENCIES,
  type Expense,
  type ExpenseCategory,
  type ExpenseInput,
  expenseInput,
  PAYMENT_METHODS,
} from '@facture/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  HardHatIcon,
  InboxIcon,
  PaperclipIcon,
  PlusIcon,
  TagsIcon,
  Trash2Icon,
  WalletMinimalIcon,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Cell, Tooltip as ChartTooltip, Pie, PieChart, ResponsiveContainer } from 'recharts'
import { toast } from 'sonner'
import { z } from 'zod'
import { CategoriesDialog } from '@/components/forms/categories-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { confirm } from '@/components/ui/confirm'
import { columnHelper, DataTable, useDebounced } from '@/components/ui/data-table'
import { DatePicker } from '@/components/ui/date-picker'
import { Field } from '@/components/ui/field'
import {
  ClearFilters,
  FilterBar,
  FilterDateRange,
  FilterSearch,
  FilterSelect,
} from '@/components/ui/filter-bar'
import { Input, Textarea } from '@/components/ui/input'
import { Switch } from '@/components/ui/misc'
import { EmptyState, Page, PageHeader } from '@/components/ui/page'
import { Select } from '@/components/ui/select'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useProjectsAccess } from '@/features/projects/access'
import { ExpenseProjectSection } from '@/features/projects/allocation-dialog'
import { projectOptionsQuery } from '@/features/projects/api'
import { CostGroupSelect, ProjectPhaseFields } from '@/features/projects/project-picker'
import { expenseCategoriesQuery, expensesQuery } from '@/features/receptions/api'
import {
  ExpenseAttachments,
  PendingAttachments,
  uploadExpenseAttachment,
} from '@/features/receptions/expense-attachments'
import { api, errorMessage } from '@/lib/api'
import { monthStartCR, todayCR } from '@/lib/dates'
import { formatDate, formatMoney } from '@/lib/format'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

const searchSchema = z.object({
  q: z.string().optional(),
  category: z.string().optional(),
  project: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.number().int().min(1).optional(),
})

export const Route = createFileRoute('/_app/expenses')({
  staticData: { crumb: 'Gastos' },
  validateSearch: searchSchema,
  component: ExpensesPage,
})

const col = columnHelper<Expense>()

function ExpensesPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const canWrite = useCan('expenses:write')
  const projectsAccess = useProjectsAccess()
  const projects = useQuery({ ...projectOptionsQuery, enabled: projectsAccess.read })
  const [text, setText] = useState(search.q ?? '')
  const q = useDebounced(text)
  const [editing, setEditing] = useState<Expense | null>(null)
  const [open, setOpen] = useState(false)
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reacciona al texto debounced
  useEffect(() => {
    if (q !== (search.q ?? '')) setSearch({ q: q || undefined, page: undefined })
  }, [q])

  const from = search.from ?? monthStartCR()
  const list = useQuery(
    expensesQuery({
      search: search.q,
      categoryId: search.category,
      projectId: projectsAccess.read ? search.project : undefined,
      from,
      to: search.to,
      page: search.page ?? 1,
      pageSize: 25,
    }),
  )
  const all = useQuery(expensesQuery({ from, to: search.to, pageSize: 200 }))
  const categories = useQuery(expenseCategoriesQuery)

  const breakdown = useMemo(() => {
    const map = new Map<string, { name: string; color: string; value: number }>()
    for (const e of all.data?.items ?? []) {
      const key = e.categoryId ?? 'none'
      const entry = map.get(key) ?? {
        name: e.categoryName ?? 'Sin categoría',
        color: e.categoryColor ?? '#94a3b8',
        value: 0,
      }
      entry.value += e.amountCrc
      map.set(key, entry)
    }
    return [...map.values()].sort((a, b) => b.value - a.value)
  }, [all.data])

  const columns = useMemo(
    () => [
      col.accessor('date', {
        header: 'Fecha',
        cell: ({ getValue }) => <span className="tabular">{formatDate(getValue())}</span>,
      }),
      col.accessor('description', {
        header: 'Descripción',
        cell: ({ row }) => (
          <div className="min-w-0 max-w-md">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium">{row.original.description}</span>
              {row.original.receptionId && <InboxIcon className="size-3.5 shrink-0 text-muted-foreground" />}
              {row.original.attachmentCount > 0 && (
                <span
                  className="inline-flex shrink-0 items-center gap-0.5 text-muted-foreground text-xs"
                  title={`${row.original.attachmentCount} adjunto(s)`}
                >
                  <PaperclipIcon className="size-3.5" />
                  {row.original.attachmentCount}
                </span>
              )}
            </div>
            <div className="truncate text-muted-foreground text-xs">{row.original.supplierName ?? '—'}</div>
          </div>
        ),
      }),
      col.accessor('categoryName', {
        header: 'Categoría',
        meta: { hideOnMobile: true },
        cell: ({ row }) =>
          row.original.categoryName ? (
            <span className="inline-flex items-center gap-2 text-sm">
              <span
                className="size-2 rounded-full"
                style={{ background: row.original.categoryColor ?? '#94a3b8' }}
              />
              {row.original.categoryName}
            </span>
          ) : (
            <Badge>Sin categoría</Badge>
          ),
      }),
      ...(projectsAccess.read
        ? [
            col.accessor('projectName', {
              header: 'Proyecto',
              meta: { hideOnMobile: true },
              cell: ({ row }) =>
                row.original.projectName ? (
                  <span
                    className="inline-flex max-w-52 items-center gap-1.5 text-sm"
                    title={row.original.projectName}
                  >
                    <HardHatIcon className="size-3.5 shrink-0 text-warning" />
                    <span className="truncate">{row.original.projectName}</span>
                    {row.original.allocatedByLines ? (
                      <span className="shrink-0 text-muted-foreground text-xs">· líneas</span>
                    ) : (
                      Math.abs(row.original.allocatedCrc - row.original.amountCrc) > 0.005 && (
                        <span className="shrink-0 text-muted-foreground text-xs">· en parte</span>
                      )
                    )}
                  </span>
                ) : (
                  <span className="text-muted-foreground text-xs">Sin proyecto</span>
                ),
            }),
          ]
        : []),
      col.accessor('amount', {
        header: 'Monto',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <span className={cn('font-medium', row.original.amount < 0 && 'text-destructive')}>
            {formatMoney(row.original.amount, row.original.currency as 'CRC')}
          </span>
        ),
      }),
    ],
    [projectsAccess.read],
  )

  return (
    <Page>
      <PageHeader
        title="Gastos"
        description="Compras y gastos del negocio. Los comprobantes aceptados en Recepción se registran solos."
        actions={
          canWrite && (
            <>
              <Button variant="outline" onClick={() => setCategoriesOpen(true)}>
                <TagsIcon /> Categorías
              </Button>
              <Button
                onClick={() => {
                  setEditing(null)
                  setOpen(true)
                }}
              >
                <PlusIcon /> Nuevo gasto
              </Button>
            </>
          )
        }
      />
      <div className="mb-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Total del período</CardTitle>
              <p className="mt-1 text-muted-foreground text-sm">
                Desde {formatDate(from)}
                {search.to ? ` hasta ${formatDate(search.to)}` : ''}
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <div className="font-semibold text-4xl tracking-tight tabular">
              {formatMoney(all.data?.totalCrc ?? 0)}
            </div>
            <div className="mt-1 text-muted-foreground text-sm">
              {all.data?.total ?? 0} gastos registrados
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-6 py-5">
            <div className="size-36 shrink-0">
              {breakdown.length > 0 ? (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={breakdown}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={42}
                      outerRadius={66}
                      paddingAngle={2}
                      strokeWidth={0}
                    >
                      {breakdown.map((b) => (
                        <Cell key={b.name} fill={b.color} />
                      ))}
                    </Pie>
                    <ChartTooltip
                      formatter={(v) => formatMoney(Number(v))}
                      contentStyle={{ borderRadius: 10, fontSize: 12 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex size-full items-center justify-center rounded-full border-8 border-muted" />
              )}
            </div>
            <div className="grid min-w-0 flex-1 gap-2">
              {breakdown.slice(0, 6).map((b) => (
                <div key={b.name} className="flex items-center gap-2 text-sm">
                  <span className="size-2.5 shrink-0 rounded-full" style={{ background: b.color }} />
                  <span className="min-w-0 flex-1 truncate">{b.name}</span>
                  <span className="text-muted-foreground tabular">{formatMoney(b.value)}</span>
                </div>
              ))}
              {breakdown.length === 0 && (
                <p className="text-muted-foreground text-sm">Sin gastos en el período.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <DataTable
        columns={columns}
        data={list.data?.items}
        loading={list.isFetching}
        getRowId={(e) => e.id}
        onRowClick={
          canWrite
            ? (e) => {
                setEditing(e)
                setOpen(true)
              }
            : undefined
        }
        toolbar={
          <FilterBar>
            <FilterSearch value={text} onChange={setText} placeholder="Buscar por gasto o proveedor…" />
            <FilterSelect
              label="Categoría"
              width="lg"
              value={search.category ?? 'all'}
              onValueChange={(v) => setSearch({ category: v === 'all' ? undefined : v, page: undefined })}
              options={[
                { value: 'all', label: 'Todas las categorías' },
                ...(categories.data ?? []).map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
            {projectsAccess.read && (
              <FilterSelect
                label="Proyecto"
                width="xl"
                value={search.project ?? 'all'}
                onValueChange={(v) => setSearch({ project: v === 'all' ? undefined : v, page: undefined })}
                options={[
                  { value: 'all', label: 'Todos los proyectos' },
                  { value: 'none', label: 'Sin proyecto' },
                  ...(projects.data ?? []).map((p) => ({ value: p.id, label: p.name })),
                ]}
              />
            )}
            <FilterDateRange
              value={{ from, to: search.to }}
              onChange={(r) => setSearch({ from: r.from, to: r.to, page: undefined })}
            />
            <ClearFilters
              active={!!(search.q || search.category || search.project || search.from || search.to)}
              onClear={() => {
                setText('')
                setSearch({
                  q: undefined,
                  category: undefined,
                  project: undefined,
                  from: undefined,
                  to: undefined,
                  page: undefined,
                })
              }}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={WalletMinimalIcon}
            title="Sin gastos en este período"
            description="Registrá un gasto o aceptá comprobantes en Recepción."
          />
        }
        pagination={
          list.data && {
            page: list.data.page,
            pageSize: list.data.pageSize,
            total: list.data.total,
            onPageChange: (p) => setSearch({ page: p === 1 ? undefined : p }),
          }
        }
      />
      <ExpenseSheet open={open} onOpenChange={setOpen} expense={editing} />
      <CategoriesDialog
        open={categoriesOpen}
        onClose={() => setCategoriesOpen(false)}
        title="Categorías de gasto"
        description="Organizá tus gastos. Al eliminar una categoría, sus gastos quedan sin categoría."
        endpoint="/expense-categories"
        queryKey={['expense-categories']}
        alsoInvalidate={[['expenses']]}
        withColor
        withCostGroup
        canWrite={canWrite}
        usageNoun={{ one: 'gasto', many: 'gastos' }}
        toCategory={(c: ExpenseCategory) => ({
          id: c.id,
          name: c.name,
          color: c.color,
          count: c.expenseCount,
          costGroupId: c.costGroupId,
        })}
      />
    </Page>
  )
}

function ExpenseSheet({
  open,
  onOpenChange,
  expense,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  expense: Expense | null
}) {
  const queryClient = useQueryClient()
  const categories = useQuery(expenseCategoriesQuery)
  const fromReception = !!expense?.receptionId
  const canWrite = useCan('expenses:write')
  const projectsAccess = useProjectsAccess()
  // Adjuntos de un gasto nuevo: se suben cuando el gasto queda guardado.
  const [pending, setPending] = useState<File[]>([])
  const form = useForm<ExpenseInput>({ resolver: zodResolver(expenseInput) })
  useEffect(() => {
    if (!open) return
    setPending([])
    form.reset(
      expense
        ? {
            date: expense.date,
            description: expense.description,
            supplierName: expense.supplierName,
            amount: expense.amount,
            taxAmount: expense.taxAmount,
            currency: expense.currency as 'CRC',
            exchangeRate: expense.currency === 'CRC' ? null : expense.exchangeRate,
            categoryId: expense.categoryId,
            paymentMethod: expense.paymentMethod,
            reference: expense.reference,
            notes: expense.notes,
            forCompany: expense.forCompany,
          }
        : {
            date: todayCR(),
            description: '',
            amount: 0,
            taxAmount: 0,
            currency: 'CRC',
            paymentMethod: '04',
            categoryId: null,
            projectId: null,
            phaseId: null,
            forCompany: false,
          },
    )
  }, [open, expense, form])

  const save = useMutation({
    mutationFn: (values: ExpenseInput) =>
      expense ? api.put<Expense>(`/expenses/${expense.id}`, values) : api.post<Expense>('/expenses', values),
    onSuccess: async (saved) => {
      toast.success(expense ? 'Gasto actualizado' : 'Gasto registrado')
      for (const file of expense ? [] : pending) {
        try {
          await uploadExpenseAttachment(saved.id, file)
        } catch (error) {
          toast.error(`No se pudo adjuntar ${file.name}: ${errorMessage(error)}`)
        }
      }
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      onOpenChange(false)
    },
  })
  const { errors } = form.formState
  const currency = form.watch('currency')

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent size="md">
        <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>{expense ? 'Editar gasto' : 'Nuevo gasto'}</SheetTitle>
            <SheetDescription>
              {fromReception
                ? 'Viene de un comprobante recibido: solo podés cambiar la descripción, la categoría y las notas.'
                : 'Gastos sin comprobante electrónico o registrados a mano.'}
            </SheetDescription>
          </SheetHeader>
          <SheetBody className="grid content-start gap-4">
            <fieldset disabled={fromReception} className="contents">
              <div className="grid grid-cols-[1fr_9rem] gap-3">
                <Field label="Proveedor" error={errors.supplierName}>
                  {(c) => <Input {...c} {...form.register('supplierName')} />}
                </Field>
                <Controller
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <Field label="Fecha" error={errors.date} required>
                      {(c) => (
                        <DatePicker
                          {...c}
                          ref={field.ref}
                          name={field.name}
                          value={field.value}
                          onChange={(v) => field.onChange(v ?? '')}
                          onBlur={field.onBlur}
                          disabled={fromReception}
                        />
                      )}
                    </Field>
                  )}
                />
              </div>
            </fieldset>
            <Field label="Descripción" error={errors.description} required>
              {(c) => <Input {...c} {...form.register('description')} />}
            </Field>
            <fieldset disabled={fromReception} className="contents">
              <div className="grid grid-cols-[1fr_1fr_7rem] gap-3">
                <Field label="Monto total" error={errors.amount} required>
                  {(c) => (
                    <Input
                      {...c}
                      {...form.register('amount')}
                      type="number"
                      step="0.01"
                      className="tabular"
                    />
                  )}
                </Field>
                <Field label="IVA incluido" error={errors.taxAmount}>
                  {(c) => (
                    <Input
                      {...c}
                      {...form.register('taxAmount')}
                      type="number"
                      step="0.01"
                      className="tabular"
                    />
                  )}
                </Field>
                <Controller
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <Field label="Moneda">
                      {(c) => (
                        <Select
                          {...c}
                          value={field.value ?? 'CRC'}
                          onValueChange={field.onChange}
                          options={CURRENCIES.map((cu) => ({ value: cu.code, label: cu.code }))}
                        />
                      )}
                    </Field>
                  )}
                />
              </div>
              {currency !== 'CRC' && (
                <Field label="Tipo de cambio" hint="Vacío = tipo de cambio del BCCR de hoy">
                  {(c) => (
                    <Input
                      {...c}
                      {...form.register('exchangeRate', { setValueAs: (v) => (v === '' ? null : Number(v)) })}
                      type="number"
                      step="0.01"
                      className="max-w-40"
                    />
                  )}
                </Field>
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Controller
                  control={form.control}
                  name="paymentMethod"
                  render={({ field }) => (
                    <Field label="Medio de pago">
                      {(c) => (
                        <Select
                          {...c}
                          value={field.value ?? '04'}
                          onValueChange={field.onChange}
                          options={PAYMENT_METHODS.map((m) => ({ value: m.code, label: m.name }))}
                        />
                      )}
                    </Field>
                  )}
                />
                <Field label="Referencia" error={errors.reference}>
                  {(c) => <Input {...c} {...form.register('reference')} />}
                </Field>
              </div>
            </fieldset>
            <Controller
              control={form.control}
              name="categoryId"
              render={({ field }) => (
                <Field label="Categoría">
                  {(c) => (
                    <Select
                      {...c}
                      value={field.value ?? ''}
                      onValueChange={field.onChange}
                      placeholder="Sin categoría"
                      options={(categories.data ?? []).map((cat) => ({ value: cat.id, label: cat.name }))}
                    />
                  )}
                </Field>
              )}
            />
            {projectsAccess.allocate && (
              <Controller
                control={form.control}
                name="forCompany"
                render={({ field }) => (
                  <label className="flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-border bg-subtle px-4 py-3">
                    <span className="grid gap-0.5">
                      <span className="font-medium text-sm">Gasto de la empresa</span>
                      <span className="text-muted-foreground text-xs">
                        No se imputa a ninguna obra. Si ya estaba en un proyecto, se quita de ahí.
                      </span>
                    </span>
                    <Switch
                      checked={field.value === true}
                      onCheckedChange={(on) => {
                        field.onChange(on)
                        if (on) {
                          form.setValue('projectId', null)
                          form.setValue('phaseId', null)
                          form.setValue('costGroupId', undefined)
                        } else if (expense) {
                          form.resetField('projectId')
                          form.resetField('phaseId')
                          form.resetField('costGroupId')
                        }
                      }}
                    />
                  </label>
                )}
              />
            )}
            {projectsAccess.allocate && !expense && !form.watch('forCompany') && (
              <div className="grid gap-3 rounded-xl border border-border p-4">
                <ProjectPhaseFields
                  projectId={form.watch('projectId')}
                  phaseId={form.watch('phaseId')}
                  onChange={(v) => {
                    form.setValue('projectId', v.projectId, { shouldDirty: true })
                    form.setValue('phaseId', v.phaseId, { shouldDirty: true })
                  }}
                  hint="Gasto del proyecto: se imputa el 100 % del monto."
                />
                {form.watch('projectId') && (
                  <Field
                    label="Grupo de costo"
                    hint="Sin clasificar no lleva administración cuando la obra usa grupos."
                  >
                    {(c) => (
                      <CostGroupSelect
                        id={c.id}
                        value={form.watch('costGroupId')}
                        onChange={(id) => form.setValue('costGroupId', id, { shouldDirty: true })}
                      />
                    )}
                  </Field>
                )}
              </div>
            )}
            {expense && projectsAccess.read && (
              <ExpenseProjectSection expenseId={expense.id} canAllocate={projectsAccess.allocate} />
            )}
            <Field label="Notas">{(c) => <Textarea {...c} {...form.register('notes')} rows={3} />}</Field>
            <div className="grid gap-2">
              <div className="font-medium text-sm">Comprobantes adjuntos</div>
              {expense ? (
                <ExpenseAttachments expenseId={expense.id} canWrite={canWrite} />
              ) : (
                <PendingAttachments files={pending} onChange={setPending} />
              )}
            </div>
          </SheetBody>
          <SheetFooter className="justify-between">
            {expense && !fromReception ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  confirm({
                    title: '¿Eliminar este gasto?',
                    destructive: true,
                    onConfirm: async () => {
                      await api.delete(`/expenses/${expense.id}`)
                      queryClient.invalidateQueries({ queryKey: ['expenses'] })
                      onOpenChange(false)
                    },
                  })
                }
              >
                <Trash2Icon /> Eliminar
              </Button>
            ) : (
              <span />
            )}
            <Button type="submit" loading={save.isPending}>
              Guardar
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
